import jsonToFormData from 'json-form-data';
import queryString from 'query-string';
import ApiError from './ApiError';
import {
  ApiRequestAddTributeParams,
  ApiRequestParams,
  ApiRequestRegionsParams,
  ApiRequestServicesParams,
  ApiResponseService,
  ApiResponseServices,
  Organization,
  Region,
} from './types';

export default class Api {
  public static host: string = '';

  public static cache = new Map();

  static setHost(host: string) {
    Api.host = host;
  }

  static async getConfig() {
    return await Api.request('embed/v1/get-config');
  }

  static createShareUrl(hash: string | undefined): URL {
    return new URL(`${Api.host}/embed/v1/services/${hash}/share?pageUrl=${encodeURI(window.location.href.split('?')[0])}`);
  }

  static async organization(hash: string): Promise<Organization> {
    return await Api.request(`embed/v1/get-org/organization/${hash}`);
  }

  static async service(hash: string, password?: string, preview?: boolean): Promise<ApiResponseService> {
    const data = {
      password,
    };

    if (preview) {
      data['preview'] = preview;
    }

    return await Api.request(`embed/v1/services/${hash}`, {
      data,
    });
  }

  static async services({ hash, ...data }: ApiRequestServicesParams): Promise<ApiResponseServices> {
    return await Api.request(`embed/v1/organizations/${hash}/services`, { data });
  }

  static async summaryServices({ hash, ...data }: ApiRequestServicesParams): Promise<ApiResponseServices> {
    return await Api.request(`embed/v1/organizations/${hash}/summary-services`, { data });
  }

  static async streamViewerCount(hash: string) {
    Api.request(`embed/v1/services/${hash}/add-unique-view`, {
      cache: 'no-store',
      method: 'POST',
    });
  }

  static async videoViewerCount(uuid: string) {
    Api.request(`embed/v1/services/${uuid}/add-video-view`, {
      cache: 'no-store',
      method: 'POST',
    });
  }

  static getICS(hash: string) {
    return Api.host + '/embed/v1/add-to-calender/' + hash;
  }

  static async regions(data: ApiRequestRegionsParams): Promise<Region[]> {
    return await Api.request(`embed/v1/regions`, { data });
  }

  static async branches(hash: string): Promise<
    {
      id: number;
      name: string;
    }[]
  > {
    return await Api.request(`embed/v1/organizations/${hash}/branches`);
  }

  static async filterData(hash: string, data: ApiRequestRegionsParams): Promise<{}[]> {
    return await Api.request(`embed/v1/${hash}/filter-data`, { data });
  }

  static async filterDataAndOrg(hash: string, data: ApiRequestRegionsParams): Promise<{}[]> {
    return await Api.request(`embed/v1/${hash}/filter-data-org`, { data });
  }

  static async addTribute({ hash, data }: ApiRequestAddTributeParams): Promise<void> {
    let options = {
      initialFormData: new FormData(),
      showLeafArrayIndexes: true,
      includeNullValues: false,
      mapping: function (value) {
        if (typeof value === 'boolean') {
          return +value ? '1' : '0';
        }
        return value;
      },
    };

    const newData = jsonToFormData(data, options);

    await Api.request(`embed/v1/services/${hash}/tributes`, {
      method: 'POST',
      data: newData,
    });
  }

  static async request(uri: string, { method = 'GET', data, ...config }: ApiRequestParams = {}) {
    let cacheKey = method === 'GET' ? `${uri}||${JSON.stringify(data)}` : null;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const fetchConfig: RequestInit = {
      ...config,
      method,
    };

    if (data instanceof FormData) {
      fetchConfig.headers = {
        ...config.headers,
        Accept: 'application/json',
      };

      fetchConfig.body = data;
    } else {
      if (['POST', 'PUT'].includes(method) && typeof data === 'object') {
        fetchConfig.body = JSON.stringify(data);
      }

      fetchConfig.headers = {
        ...config.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
    }

    const url = new URL(`${Api.host}/${uri}`);
    if (method === 'GET' && data) {
      // noinspection JSDeprecatedSymbols
      url.search = queryString.stringify(data, { arrayFormat: 'bracket' });
    }

    return await fetch(url.toString(), fetchConfig)
      .then((res) => {
        if (!res.ok) {
          throw new ApiError(res);
        }
        return res.json();
      })
      .then((res) => {
        if (method === 'GET' && !('cache' in config)) {
          this.cache.set(cacheKey, res);
        }
        return res;
      });
  }
}
